/**
 * Step 13 F-4: which V2 page instance a route should show. MainLayout keeps the V2 page alive
 * (`<KeepAlive :max="1">`) and keys it with this, so:
 * - the same conversation (switching to Job Talent / any page and back) → same key → the live,
 *   cached instance comes back with its in-flight reply / search and chat scroll position;
 * - a different job, or a new conversation → a new key → a fresh instance, and `max: 1` destroys the
 *   previous one (its unmount hooks run: run aborted, step 12 refresh disposed).
 *
 * A new conversation gets its job id from the page itself (`onConversationCreated`): the page
 * `adopt`s that id before it puts it in the URL, so the jobId change does not remount it mid-reply.
 */
import { ref, watch, type Ref } from 'vue'

export type RecruitingAiV2Instance = {
  key: string
  conversationId: string
}

const normalizeId = (value: unknown) => String(value ?? '').trim()

/** Pure decision: reuse `current` when it already shows `jobId`, else a fresh instance (`seq`). */
export function resolveRecruitingAiV2Instance(
  current: RecruitingAiV2Instance | null,
  jobId: unknown,
  seq: number,
): { instance: RecruitingAiV2Instance; reused: boolean } {
  const conversationId = normalizeId(jobId)
  if (current && current.conversationId === conversationId) return { instance: current, reused: true }
  return { instance: { key: `recruiting-ai-v2:${seq}`, conversationId }, reused: false }
}

export function createRecruitingAiV2InstanceTracker() {
  let current: RecruitingAiV2Instance | null = null
  let seq = 0

  /** The cache key for a V2 route showing `jobId` ('' = new conversation). */
  function keyFor(jobId: unknown) {
    const { instance, reused } = resolveRecruitingAiV2Instance(current, jobId, seq + 1)
    if (!reused) seq += 1
    current = instance
    return instance.key
  }

  /** The live instance now owns `conversationId` (it just created it). */
  function adopt(conversationId: unknown) {
    const id = normalizeId(conversationId)
    if (current && id) current = { ...current, conversationId: id }
  }

  return { keyFor, adopt, current: () => current }
}

/** Shared by MainLayout (keys the cached page) and the V2 page (adopts its new conversation). */
export const recruitingAiV2InstanceTracker = createRecruitingAiV2InstanceTracker()

export const RECRUITING_AI_V2_VIEW_NAME = 'RecruitingAiV2View'

/** Component names MainLayout's KeepAlive caches: only the V2 Agent page. */
export const KEEP_ALIVE_MANAGE_VIEWS: string[] = [RECRUITING_AI_V2_VIEW_NAME]

/** MainLayout's routed-view key: the V2 instance key on the V2 page, the path elsewhere (unchanged). */
export const getManageViewKey = (route: { name?: unknown; path: string }, recruitingAiV2Key: string) =>
  route.name === 'RecruitingAiV2' && recruitingAiV2Key ? recruitingAiV2Key : route.path

export type RecruitingAiV2InstanceTracker = ReturnType<typeof createRecruitingAiV2InstanceTracker>

/**
 * MainLayout's V2 instance key. Updated by a pre-flush watcher (before the routed view re-renders) and
 * only when a V2 route's job changes, so the page adopting its new conversation id (then replacing the
 * URL) keeps its key, and leaving the V2 page keeps the cached key for coming back.
 */
export function useRecruitingAiV2InstanceKey(
  route: { name?: unknown; query: Record<string, unknown> },
  tracker: RecruitingAiV2InstanceTracker = recruitingAiV2InstanceTracker,
): Ref<string> {
  const key = ref('')
  watch(
    () => [route.name, route.query.jobId] as const,
    ([name, jobId]) => {
      if (name !== 'RecruitingAiV2') return
      key.value = tracker.keyFor(Array.isArray(jobId) ? jobId[0] : jobId)
    },
    { immediate: true },
  )
  return key
}
